import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ArrowRight, Clapperboard, Clock3, KeyRound, LockKeyhole, Scale, Trophy, Unlock } from 'lucide-react';
import HeroBackground from '../components/ui/HeroBackground';
import HomeFilm from '../components/home/HomeFilm';
import CastFigure from '../motion/CastFigure';
import { castKey } from '../data/cast';
import {
  Colon,
  CountdownUnit,
  CoverLine,
  FolioSection,
  HighlightTile,
  SectionMast,
  ShameCard,
  YearbookRow,
} from '../components/home/HomeEditorial';
import { StatCarousel } from '../components/home/StatCarousel';
import { loadChampionships } from '../utils/championships';
import { loadRoasts } from '../utils/roasts';
import { getManagerImage, getManagerEmoji } from '../utils/managers';
import {
  GROUP_CHAT_ARGUMENTS,
  KEEPER_TILES,
  NUMBER_TILES,
  SHAME_CARDS,
  YEARBOOK_ENTRIES,
} from '../data/homeAlmanac';
import { pickTickerFacts, roastsToTiles, weaveCarouselTiles } from '../utils/homeAlmanac';
import { GAUNTLET_TARGET_STREAK } from '../utils/keeperGauntlet';
import { CURRENT_SEASON } from '../config/season';
import { pickDaily, MASTHEAD_VARIANTS } from '../utils/rotatingCopy';
import { TAPE_DESK, ranksFromLast } from '../data/theTape';
import draftGrades from '../data/draftGrades.json';
import predictionIssues from '../data/predictionIssues.json';

const DRAFT_PICK_COUNT = draftGrades.draftedPickCount;
const DRAFT_TEAM_COUNT = draftGrades.teams.length;
const DRAFT_INPUT_COUNT = Object.keys(draftGrades.methodology.weights).length;
const DRAFT_TEAM_COUNT_WORD = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'][DRAFT_TEAM_COUNT];

// Normalize any tile shape (hero/medium/small stat, quote, split) into a
// uniform 1-column "small" stat so the carousel's 6-col grid fills every page
// without trailing whitespace. Preserves the tile's data; just collapses span
// and (for quotes/splits) reformats into a stat-shaped tile.
function normalizeTile(tile) {
  if (tile.kind === 'quote') {
    return {
      kind: 'stat',
      span: 'small',
      tone: tile.tone || 'dark',
      mark: 'left-rule',
      value: '“',
      unit: tile.label || 'Quote',
      caption: tile.text,
      sub: tile.cite ? `- ${tile.cite}` : undefined,
    };
  }
  if (tile.kind === 'split') {
    // Split tiles compare two stats side-by-side. Emit two small tiles instead.
    return [
      {
        kind: 'stat',
        span: 'small',
        tone: tile.tone || 'dark',
        mark: 'top-rule',
        value: tile.left?.value || '—',
        unit: tile.left?.label || 'Stat',
        caption: tile.left?.sub || '',
      },
      {
        kind: 'stat',
        span: 'small',
        tone: tile.tone || 'dark',
        mark: 'top-rule',
        value: tile.right?.value || '—',
        unit: tile.right?.label || 'Stat',
        caption: tile.right?.sub || '',
      },
    ];
  }
  return { ...tile, span: 'small' };
}

const KEEPER_VAULT_STATS = [
  {
    icon: Trophy,
    value: String(GAUNTLET_TARGET_STREAK),
    label: 'straight answers',
    copy: 'Miss once and the run resets.',
  },
  {
    icon: Clock3,
    value: '20',
    label: 'seconds each',
    copy: 'No lookup time, no group chat help.',
  },
  {
    icon: LockKeyhole,
    value: '200+',
    label: 'league receipts',
    copy: 'Champions, drafts, scores, team names, and rivalries.',
  },
  {
    icon: KeyRound,
    value: '1',
    label: 'keeper decision',
    copy: 'The prize is research built to help choose next season\'s keeper.',
  },
];

export default function Home() {
  const [championships, setChampionships] = useState([]);
  const [roasts, setRoasts] = useState(null);

  useEffect(() => {
    Promise.all([
      loadChampionships().catch(() => []),
      loadRoasts().catch(() => null),
    ]).then(([ch, rt]) => {
      setChampionships(ch);
      setRoasts(rt);
    });
  }, []);

  const tickerFacts = useMemo(() => pickTickerFacts(roasts), [roasts]);
  // Merge the curated NUMBER_TILES with everything we have logged in roasts.json
  // (heartbreaks, blowouts, streaks, rivalries, dynasty facts, droughts,
  // chokes, trivia). Normalize every tile to a uniform "small" 1-column span
  // so each carousel page fills its 6-col grid cleanly — mixed-span tiles were
  // leaving visible whitespace on certain pages.
  // Merge curated NUMBER_TILES with roast-derived tiles, but interleave
  // them so adjacent cards look distinct (no long runs of same style).
  const carouselTiles = useMemo(
    () => weaveCarouselTiles(NUMBER_TILES, roastsToTiles(roasts)).flatMap(normalizeTile),
    [roasts]
  );
  const championsRibbon = championships.slice().reverse();
  const latestYear = championsRibbon.length > 0 ? championsRibbon[championsRibbon.length - 1].year : null;
  const completedSeasons = championships.length || 13;
  const championCount = championships.length
    ? new Set(championships.map((c) => c.champion)).size
    : 7;

  const masthead = pickDaily(MASTHEAD_VARIANTS);
  return (
    <>
      <Link to="/ai-picks" className="group flex flex-wrap items-center justify-between gap-3 border-y border-accent-gold/40 bg-accent-gold px-6 py-4 text-bg-deep md:px-14" aria-label={`Read the Week ${predictionIssues.latest.week} AI Picks`}>
        <span className="font-display text-lg font-black uppercase tracking-wide">Week {predictionIssues.latest.week} · The Forecast</span>
        <span className="font-body text-sm">Five matchups. Independent picks. Every starter scored.</span>
        <span className="inline-flex items-center gap-2 font-display text-xs font-black uppercase tracking-widest">Read the picks <ArrowRight size={16} /></span>
      </Link>
      <HomeFilm />
      <HeroBackground variant="gold" className="scroll-mt-16">
        <div id="almanac" className="absolute top-0" aria-hidden="true" />

        <div className="relative border-b border-accent-gold/30 px-6 md:px-14 py-3 flex items-center justify-between gap-4 text-[10px] md:text-[11px] tracking-[0.25em] font-display font-bold uppercase">
          <div className="flex items-center gap-3 text-accent-gold">
            <span className="font-mono text-[10px]">{CURRENT_SEASON.issueVolume}</span>
            <span className="text-accent-gold/40">/</span>
            <span className="font-mono text-[10px]">{CURRENT_SEASON.issueNumber}</span>
          </div>
          <div className="hidden md:block text-center text-ink-primary tracking-[0.4em]">
            THE BRUINS SPORTS ALMANAC
          </div>
          <div className="text-right text-ink-secondary">
            <span className="font-mono text-[10px]">{CURRENT_SEASON.year}</span>
            <span className="hidden md:inline"> · SEASON ISSUE</span>
          </div>
        </div>

        <div className="relative px-6 md:px-14 pt-8 md:pt-10 pb-10">
          <div className="almanac-rise" style={{ animationDelay: '0.05s' }}>
            <div className="flex items-center gap-3 mb-5">
              <span className="block h-px w-10 bg-accent-gold" />
              <span className="font-display tracking-[0.35em] text-[11px] text-accent-gold font-bold uppercase">
                {masthead.eyebrow}
              </span>
            </div>

            <h1 className="font-display font-black uppercase leading-[0.88] tracking-normal text-ink-primary"
                style={{ fontSize: 'clamp(48px, 8vw, 110px)' }}>
              <span className="block">{masthead.lineA}</span>
              <span className="block text-accent-gold relative">
                {masthead.lineB}
                <span
                  aria-hidden="true"
                  className="absolute inset-0 bg-halftone opacity-30 mix-blend-overlay pointer-events-none"
                  style={{ backgroundSize: '6px 6px' }}
                />
              </span>
            </h1>

            <p className="mt-6 font-body italic text-ink-secondary leading-snug font-medium max-w-[44ch] text-xl md:text-2xl">
              {masthead.dek}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-display tracking-[0.25em] uppercase text-ink-tertiary">
              <span>By <span className="text-ink-primary">The Editors</span></span>
              <span className="text-accent-gold/50">·</span>
              <span className="font-mono text-[10px]">{CURRENT_SEASON.kickoff.monthLabel}</span>
              <span className="text-accent-gold/50">·</span>
              <span>Filed from Brentwood</span>
            </div>
          </div>

          <TapeDeskLaunch />
          <BureauLaunch />
          <KeeperVaultLaunch />

          <div className="mt-8 md:mt-10 grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 almanac-rise" style={{ animationDelay: '0.2s' }}>
            <CoverLine
              eyebrow="THE LEAD"
              borderClass="border-accent-gold"
              eyebrowClass="text-accent-gold"
              title="The grades are in."
              blurb={`${DRAFT_INPUT_COUNT} measured inputs, ${DRAFT_TEAM_COUNT} composite scores, and a full draft board available for appeal.`}
            />
            <CoverLine
              eyebrow="COVER STORY"
              borderClass="border-ink-tertiary/60"
              eyebrowClass="text-ink-tertiary"
              title={`${DRAFT_TEAM_COUNT_WORD.charAt(0).toUpperCase()}${DRAFT_TEAM_COUNT_WORD.slice(1)} keepers were filed.`}
              blurb="Bijan cost a first. Judkins cost a fourteenth. Robby kept Colston Loveland in round fifteen. Every price is on the board."
            />
            <CoverLine
              eyebrow="THE TAPE"
              borderClass="border-accent-red"
              eyebrowClass="text-accent-red"
              title="Teich vs Krow is on file."
              blurb="Kyle ranked himself first at 50.01 percent. Independent review returned a B-plus. The prior testimony remains available."
            />
            <CoverLine
              eyebrow="RECEIPTS"
              borderClass="border-ink-tertiary/60"
              eyebrowClass="text-ink-tertiary"
              title="Every 2026 pick was scored."
              blurb={`All ${DRAFT_PICK_COUNT} selections, the ${DRAFT_INPUT_COUNT}-input methodology, and the full standings now live in the Registrar.`}
            />
          </div>
        </div>

        <div className="relative h-px border-t border-dashed border-accent-gold/30" />
      </HeroBackground>

      <div className="bg-bg-deep border-t border-accent-gold/40 border-b border-accent-gold/40 relative">
        <div className="relative overflow-hidden marquee-pause">
          <span aria-hidden="true" className="pointer-events-none absolute left-0 top-0 h-full w-16 bg-gradient-to-r from-bg-deep to-transparent z-10" />
          <span aria-hidden="true" className="pointer-events-none absolute right-0 top-0 h-full w-16 bg-gradient-to-l from-bg-deep to-transparent z-10" />
          <div className="absolute left-0 top-0 z-20 h-full flex items-center px-4 md:px-6 bg-accent-gold text-bg-deep">
            <span className="font-display font-black uppercase tracking-[0.3em] text-[10px] md:text-xs">
              League Facts
            </span>
          </div>
          <div className="marquee-track py-3 pl-44 md:pl-56 font-display tracking-[0.25em] uppercase text-[12px] md:text-[13px] text-ink-secondary">
            {[...tickerFacts, ...tickerFacts].map((fact, i) => (
              <span key={i} className="flex items-center gap-8">
                <span className="text-accent-gold">&#x25B8;</span>
                <span>{fact}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      <FolioSection numeral="03" align="left" className="bg-bg-deep">
        <SectionMast
          tag="League Stats"
          title="Receipts Worth Bringing Up."
          dek="The stats most likely to get mentioned when the group starts arguing: records, bad beats, rivalry math, and trophy evidence."
          note={`Auto-cycling receipts. ${carouselTiles.length} stats rotate through the page every 30 seconds. Hover to pause and read.`}
        />
        <StatCarousel tiles={carouselTiles} />
      </FolioSection>

      <FolioSection numeral="04" align="right" className="bg-gradient-to-b from-bg-base to-bg-paper">
        <SectionMast
          tag="Friend Group Scouting Report"
          title="Manager Yearbook."
          dek="One quick read on each manager: the role they play in the league, the receipt everyone remembers, and the thing they have to answer for this season."
          note="No invented grades here. This is the part built for people to find themselves, laugh, and immediately argue about the caption."
          color="crimson"
        />
        <div className="mt-12 divide-y divide-accent-gold/20 border-y border-accent-gold/20">
          {YEARBOOK_ENTRIES.map((entry, i) => (
            <YearbookRow
              key={entry.name}
              entry={entry}
              index={i}
            />
          ))}
        </div>
      </FolioSection>

      <FolioSection numeral="05" align="left">
        <SectionMast
          tag="Post-Draft Record"
          title={<>Keeper Ledger.</>}
          dek={`The first keeper draft is complete. ${DRAFT_TEAM_COUNT} keeper prices were applied, all ${DRAFT_PICK_COUNT} selections were filed, and the grades are published.`}
          note="The keeper rule shaped the board. The Registrar now measures what each manager did with it."
          action={{ label: 'All draft grades', href: '/draft-grades' }}
        />
        <div className="mt-12 grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4 auto-rows-auto md:auto-rows-[188px]">
          {KEEPER_TILES.map((tile, i) => (
            <HighlightTile key={i} tile={tile} />
          ))}
        </div>
      </FolioSection>

      <FolioSection numeral="06" align="right" className="bg-bg-deep">
        <SectionMast
          tag="Group Chat Fuel"
          title="Arguments to Restart."
          dek="The standings will change later. These are the arguments the league can start having immediately."
          note="This section is intentionally not a ranking. It is a menu of disputes, excuses, and talking points."
        />
        <div className="mt-12 grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4 auto-rows-auto md:auto-rows-[188px]">
          {GROUP_CHAT_ARGUMENTS.map((tile, i) => (
            <HighlightTile key={i} tile={tile} />
          ))}
        </div>
      </FolioSection>

      <FolioSection numeral="07" align="left" className="bg-gradient-to-b from-bg-deep to-bg-base">
        <SectionMast
          tag="Hall of Shame"
          title="Painful Awards."
          dek="The cards the tape keeps replaying: heartbreaks, collapses, and the new exhibits from this week’s ranking series."
          note="These are not season awards. They are memorable bad beats and public bits: the kind of moments people keep bringing up years later."
          color="crimson"
          action={{ label: 'All awards', href: '/champions' }}
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-7">
          {SHAME_CARDS.map((card, i) => (
            <ShameCard key={i} card={card} />
          ))}
        </div>
      </FolioSection>

      <FolioSection numeral="08" align="right">
        <SectionMast
          tag="The Trophy Case"
          title={`${completedSeasons} trophies. ${championCount} winners.`}
          dek="Every completed season champion, shown from oldest to newest."
          note="Each square is one season. The year is below the photo, and the newest champion is highlighted in gold."
          action={{ label: 'Every champion', href: '/champions' }}
        />
        <div className="mt-12 grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-11 gap-2 md:gap-3">
          {championsRibbon.map((c) => {
            const image = getManagerImage(c.champion);
            const isLatest = c.year === latestYear;
            const yearShort = `'${String(c.year).slice(-2)}`;
            return (
              <div key={`${c.year}-${c.champion}`} className="text-center">
                {castKey(c.champion) ? (
                  <div className={`w-full aspect-square paper border-t-[3px] border-accent-gold flex items-end justify-center overflow-hidden ${
                    isLatest ? 'shadow-[0_0_24px_rgba(251,191,36,0.4)]' : 'grayscale-[40%] contrast-[1.05]'
                  }`}>
                    <CastFigure name={c.champion} alt={c.champion} trigger="hover" sequence={['celebrate', 'smug']} className="w-[88%] h-[94%] translate-y-[3%]" />
                  </div>
                ) : image ? (
                  <img
                    src={`/images/managers/${encodeURIComponent(image)}`}
                    alt={c.champion}
                    className={`w-full aspect-square object-cover border-t-[3px] border-accent-gold ${
                      isLatest ? 'shadow-[0_0_24px_rgba(251,191,36,0.4)]' : 'grayscale-[40%] contrast-[1.05]'
                    }`}
                  />
                ) : (
                  <div className="w-full aspect-square bg-gradient-to-br from-bg-elevated to-bg-surface border-t-[3px] border-accent-gold flex items-center justify-center text-4xl grayscale-[30%]">
                    {getManagerEmoji(c.champion, '\u{1F3C6}')}
                  </div>
                )}
                <div className="mt-2 font-mono text-[10px] text-accent-gold tracking-[0.15em] font-bold">
                  {yearShort}
                </div>
                <div className={`font-display tracking-[0.1em] text-[11px] uppercase font-bold ${
                  isLatest ? 'text-accent-gold' : 'text-ink-primary'
                }`}>
                  {c.champion}
                </div>
              </div>
            );
          })}
        </div>
      </FolioSection>

      <section className="relative overflow-hidden bg-bg-deep">
        <div
          aria-hidden="true"
          className="absolute -top-12 -right-8 font-display font-black text-accent-gold/[0.05] leading-none select-none pointer-events-none"
          style={{ fontSize: 'clamp(180px, 24vw, 320px)' }}
        >
          09
        </div>
        <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(251,191,36,0.10),transparent_70%)] pointer-events-none" />
        <div className="relative px-6 md:px-14 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="block h-px w-8 bg-accent-gold" />
            <span className="font-display tracking-[0.35em] text-[11px] text-accent-gold font-bold uppercase">
              Post-Draft Status
            </span>
            <span className="block h-px w-8 bg-accent-gold" />
          </div>
          <div className="flex justify-center items-start gap-4 md:gap-8 mb-8">
            <CountdownUnit value={String(DRAFT_PICK_COUNT)} label="Picks Filed" />
            <Colon />
            <CountdownUnit value="LIVE" label="Grades" />
            <Colon />
            <CountdownUnit value="SEPT 10" label="Kickoff" />
          </div>
          <p className="font-body italic text-ink-secondary text-base md:text-lg max-w-[58ch] mx-auto leading-snug">
            The board is closed, all {DRAFT_TEAM_COUNT} keeper decisions are in the record, and the season opens Thursday, September 10.
          </p>
        </div>
      </section>
    </>
  );
}

export function DraftWeekendStrip() {
  const [now, setNow] = useState(() => new Date());
  const draftAt = useMemo(() => new Date(CURRENT_SEASON.draftAt), []);
  const over = draftAt - now <= 0;
  useEffect(() => {
    if (over) return undefined;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [over]);
  const ms = Math.max(0, draftAt - now);
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  const cards = [
    { to: '/draft-room', label: 'THE WAR ROOM', blurb: 'One table. Seventeen rounds. Everyone comes armed.', image: '/videos/draft/war-room-poster.webp' },
    { to: '/draft-room', label: 'DRAFT ORDER', blurb: 'The 17-round custom order is published. First pick comes after 19 players are gone.' },
    { to: '/draft-room', label: 'KEEPER RULES', blurb: 'One keeper each. The price is the round he went in last year.' },
  ];
  return (
    <section
      className="border-b border-accent-gold/40 bg-bg-deep px-6 py-10 md:px-14 md:py-12"
      aria-labelledby="draft-weekend-title"
      data-testid="draft-weekend-strip"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-8 lg:flex-row lg:items-center lg:gap-12">
        <div className="shrink-0">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-accent-red">
            <span className="live-dot mr-2 inline-block align-middle" aria-hidden="true" />Draft weekend
          </p>
          <h2 id="draft-weekend-title" className="mt-3 font-display text-4xl font-black uppercase leading-[0.9] md:text-5xl">
            Saturday.
            <span className="block text-accent-gold">2:00 PM PDT.</span>
          </h2>
          <p className="mt-4 font-mono text-sm font-bold tracking-[0.14em] text-ink-secondary" aria-live="off">
            {days}D · {String(hours).padStart(2, '0')}H · {String(mins).padStart(2, '0')}M · {String(secs).padStart(2, '0')}S
          </p>
        </div>
        <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-3">
          {cards.map((card) => (
            <Link
              key={card.label}
              to={card.to}
              className="group relative overflow-hidden border border-accent-gold/40 bg-bg-surface p-4 transition-colors hover:border-accent-gold"
            >
              {card.image && (
                <img src={card.image} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-25 transition-opacity group-hover:opacity-40" />
              )}
              <div className="relative">
                <div className="font-display text-[13px] font-black uppercase tracking-[0.22em] text-accent-gold">{card.label}</div>
                <p className="mt-2 font-body text-sm italic leading-snug text-ink-secondary">{card.blurb}</p>
                <div className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink-tertiary group-hover:text-accent-gold">
                  Enter <ArrowRight size={12} strokeWidth={3} aria-hidden="true" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function BureauLaunch() {
  return (
    <section
      aria-labelledby="bureau-launch"
      className="almanac-rise relative mt-5 overflow-hidden border-2 border-accent-gold bg-bg-deep"
      style={{ animationDelay: '0.2s' }}
      data-testid="bureau-launch"
    >
      <div className="relative p-5 md:p-7">
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 bg-accent-gold px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.25em] text-bg-deep">
            <Scale size={15} strokeWidth={2.5} aria-hidden="true" />
            New ledger toys
          </span>
        </div>
        <h2
          id="bureau-launch"
          className="mt-4 font-display font-black uppercase leading-[0.86] tracking-normal"
          style={{ fontSize: 'clamp(28px, 4.4vw, 52px)' }}
        >
          Luck. Curse. Sacko. Awards.
        </h2>
        <p className="mt-3 max-w-[64ch] font-body italic text-ink-secondary">
          Play-the-field luck from 875 games, the regular-season curse board, thirteen years of last place, and an awards show with no rings.
        </p>
        <Link
          to="/bureau"
          className="mt-5 inline-flex items-center gap-2 bg-accent-gold px-5 py-3 font-display text-[12px] font-black uppercase tracking-[0.24em] text-bg-deep hover:bg-ink-primary transition-colors"
        >
          Open the Bureau
          <ArrowRight size={16} strokeWidth={3} aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}

function TapeDeskLaunch() {
  const board = ranksFromLast();
  return (
    <section
      aria-labelledby="tape-desk-launch"
      className="almanac-rise relative mt-5 overflow-hidden border-2 border-accent-red bg-bg-deep"
      style={{ animationDelay: '0.14s' }}
      data-testid="tape-desk-launch"
    >
      <div className="relative p-5 md:p-7">
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 bg-accent-red px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.25em] text-ink-primary">
            <Clapperboard size={15} strokeWidth={2.5} aria-hidden="true" />
            New comedy desk
          </span>
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-accent-red">
            {TAPE_DESK.asOfLabel}
          </span>
        </div>
        <h2
          id="tape-desk-launch"
          className="mt-4 max-w-[900px] font-display font-black uppercase leading-[0.86] tracking-normal text-ink-primary"
          style={{ fontSize: 'clamp(32px, 5vw, 64px)' }}
        >
          Kyle ranks. AJ reacts.
        </h2>
        <p className="mt-3 max-w-[62ch] font-body text-base italic leading-snug text-ink-secondary md:text-xl">
          Ten of ten on the record: Daddy last, Commish ninth, the Buns eighth, Deportivo seventh, the Maestro sixth, the Grinders fifth, Doster fourth, AJ third, Anna second, Teich first at 50.01%. The camera bag is empty.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {board.map((entry) => (
            <Link
              key={entry.rank}
              to={`/the-tape#rank-${entry.rank}`}
              className="border border-accent-red/50 px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.18em] hover:bg-accent-red hover:text-ink-primary transition-colors"
            >
              #{entry.rank} {entry.manager}
            </Link>
          ))}
        </div>
        <Link
          to="/the-tape"
          className="mt-5 inline-flex items-center gap-2 bg-accent-red px-5 py-3 font-display text-[12px] font-black uppercase tracking-[0.24em] text-ink-primary hover:bg-ink-primary hover:text-bg-deep transition-colors"
        >
          Open the tape
          <ArrowRight size={16} strokeWidth={3} aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}

function KeeperVaultLaunch() {
  return (
    <section
      aria-labelledby="keeper-vault-launch"
      className="almanac-rise relative mt-8 md:mt-10 overflow-hidden border-2 border-accent-gold bg-bg-deep shadow-[0_0_40px_rgba(251,191,36,0.18)]"
      style={{ animationDelay: '0.22s' }}
    >
      <div aria-hidden="true" className="absolute inset-0 bg-gold-radial opacity-80" />
      <div aria-hidden="true" className="absolute inset-0 bg-gridiron" />
      <div className="relative grid gap-0 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="p-5 md:p-7 lg:p-8">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 bg-accent-red px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.25em] text-ink-primary">
              <KeyRound size={15} strokeWidth={2.5} aria-hidden="true" />
              New Keeper Feature
            </span>
            <span className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-accent-gold">
              Offseason archive challenge
            </span>
            <Link
              to="/keeper-vault"
              className="inline-flex items-center gap-2 border border-accent-gold px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-accent-gold transition-colors hover:bg-accent-gold hover:text-bg-deep"
            >
              <Unlock size={14} strokeWidth={3} aria-hidden="true" />
              Start quiz
            </Link>
          </div>

          <h2
            id="keeper-vault-launch"
            className="mt-4 max-w-[900px] font-display font-black uppercase leading-[0.86] tracking-normal text-ink-primary"
            style={{ fontSize: 'clamp(42px, 6.4vw, 92px)' }}
          >
            Keeper Vault Gauntlet is live.
          </h2>

          <p className="mt-4 max-w-[70ch] font-body text-lg italic leading-snug text-ink-secondary md:text-2xl">
            Pick your manager, answer three league-history questions in a row, and unlock keeper research built to help decide who you should carry into next season.
          </p>

          <div className="mt-5 max-w-[76ch] border-l-2 border-accent-gold pl-4 font-body text-sm leading-relaxed text-ink-tertiary md:text-base">
            Winning is not just trivia bragging rights. The unlocked research will compare your eligible keepers against last year&apos;s draft cost, current 2026 projections, ADP, depth-chart role, injury risk, and this league&apos;s scoring settings.
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link
              to="/keeper-vault"
              className="inline-flex w-full items-center justify-center gap-3 bg-accent-gold px-5 py-4 font-display text-[13px] font-black uppercase tracking-[0.24em] text-bg-deep transition-colors hover:bg-ink-primary sm:w-auto"
            >
              <Unlock size={18} strokeWidth={3} aria-hidden="true" />
              Take the keeper quiz
              <ArrowRight size={18} strokeWidth={3} aria-hidden="true" />
            </Link>
            <Link
              to="/ai-picks"
              className="inline-flex w-full items-center justify-center gap-3 border border-accent-gold px-5 py-4 font-display text-[13px] font-black uppercase tracking-[0.24em] text-accent-gold transition-colors hover:bg-accent-gold hover:text-bg-deep sm:w-auto"
            >
              Read the latest AI picks
              <ArrowRight size={18} strokeWidth={3} aria-hidden="true" />
            </Link>
            <span className="font-body text-sm italic leading-snug text-ink-tertiary">
              Clear it to open your keeper decision hub. Final player verdicts come after fresh projections, ADP, ranks, and player news are loaded.
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 border-t border-accent-gold/40 lg:border-l lg:border-t-0">
          {KEEPER_VAULT_STATS.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.label}
                data-testid={stat.label === 'straight answers' ? 'keeper-vault-streak-stat' : undefined}
                className="grid grid-cols-[64px_minmax(0,1fr)] border-b border-accent-gold/25 last:border-b-0"
              >
                <div className="flex items-center justify-center border-r border-accent-gold/25 text-accent-gold">
                  <Icon size={24} strokeWidth={2.4} aria-hidden="true" />
                </div>
                <div className="min-w-0 px-4 py-4">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-mono text-3xl font-black leading-none text-ink-primary">
                      {stat.value}
                    </span>
                    <span className="font-display text-[11px] font-black uppercase tracking-[0.22em] text-accent-gold">
                      {stat.label}
                    </span>
                  </div>
                  <p className="mt-1 font-body text-sm italic leading-snug text-ink-tertiary">
                    {stat.copy}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
